import api from "./api";
import { listPayments, toManualPaymentFormData } from "./paymentService";

// Membership renewal flow (Request -> pay -> Active, new membership dates),
// backed by MembershipRenewalController. Distinct resource from the
// registration payment flow in paymentService.js — a renewal always starts
// from an existing Active/Expired member, not a new applicant.
//
// Like club renewals (clubRenewalService.js), this DOES support real online
// (CyberExt) card payment: MembershipRenewalController::formatRenewal() calls
// PaymentGatewayPayload::make() and returns a payment_payload on every
// pending renewal (store/show/myRenewals alike), and
// UnifiedPaymentConfirmController::confirm() already routes a
// membership_renewal_process payment_reference to
// MembershipRenewalController::confirmRenewalByGateway() -> the same
// completeRenewal() manualConfirm uses, just tagged "Card Payment". The
// member picks a method via MembershipRenewalPaymentModal.jsx
// (src/components/common/), mirroring ClubRenewalPaymentModal.jsx's
// gateway-popup + polling UX via checkRenewalPaymentStatus below.
// manualConfirm (shroff) still works exactly as before and takes the
// renewal's payment_reference directly, resolved via a search-by-reference
// step (searchRenewalPayment below) that mirrors the member/club
// registration Confirm Payment flow.
//
// Two independent callers:
//   - A portal member (student/independent/coach token) requesting/tracking
//     their own renewal via the /member/renewals endpoints below — the
//     backend resolves member_id from their token.
//   - A system user with "view_payments"/"submit_manual_payment_receipts"
//     (e.g. Instructor) browsing the renewal-payments ledger and confirming a
//     shroff-counter payment on a member's behalf.
//
// A manual-confirm call completes the renewal payment immediately. It also
// queues a manual_payment_receipts row for Director/Admin audit-only
// acknowledgement through the existing shared queue in paymentService.js
// (listManualPaymentApprovals/approveManualPaymentApproval/
// rejectManualPaymentApproval) — this file has no approve/reject functions
// of its own, and that step no longer gates the payment either way.

// GET /api/member/renewals — portal-member only (403 if the token doesn't
// belong to a member). Returns an array of this member's own renewals,
// newest first: {renewal_id, member_id, member_code, income_id,
// payment_reference, external_payment_reference, payable_amount,
// paid_amount, payment_status_id, payment_method_id, process_status_id,
// old_membership_start_date, old_membership_end_date,
// new_membership_start_date, new_membership_end_date, paid_at, created_at,
// updated_at}.
export const getMyRenewals = () => api.get("/api/member/renewals").then((r) => (Array.isArray(r.data) ? r.data : []));

// POST /api/member/renewals — no body. If a Pending (unpaid) renewal already
// exists for this member, returns the existing one (200) instead of erroring
// — safe to call repeatedly without checking first. Otherwise creates a new
// renewal (201) with new_membership_start_date/end_date computed from the
// member's current membership_end_date. Returns { message, renewal }, same
// row shape as one entry from getMyRenewals above.
export const requestRenewal = () => api.post("/api/member/renewals").then((r) => r.data);

// GET /api/member/renewals/{id}/shroff-voucher — the member's own printable
// pay-at-counter voucher PDF for this renewal's fee. responseType "blob" so
// axios doesn't try to JSON-parse the PDF.
export const getMyRenewalShroffVoucherPdf = (renewalId) =>
  api.get(`/api/member/renewals/${renewalId}/shroff-voucher`, { responseType: "blob" }).then((r) => r.data);

// GET /api/renewal-payments — the full renewal ledger (every renewal, not
// just a single lookup), gated by "view_payments". Optional { search }
// filter (matches payment_reference, member id/initials/lastname/email/nic —
// see MembershipRenewalController::index). Returns a plain array, same row
// shape as getMyRenewals above plus payment_status/process_status
// description strings and the member's title/initials/lastname/email/
// nic_number.
export const listRenewalPayments = (params = {}) =>
  api.get("/api/renewal-payments", { params }).then((r) => (Array.isArray(r.data) ? r.data : []));

// GET /api/renewal-payments/{id} — single row, same shape as one entry from
// listRenewalPayments above (gated by "view_payment_details").
export const getRenewalPayment = (renewalId) => api.get(`/api/renewal-payments/${renewalId}`).then((r) => r.data);

// Looks up a single renewal by the reference number printed on its shroff
// voucher, for the Confirm Payment tab's search-then-confirm flow.
// MembershipRenewalController::formatRenewal() (shared by listRenewalPayments
// and getRenewalPayment above) never includes the member's name — only
// member_code — so this instead reuses GET /api/payments?type=renewal&search=
// (PaymentController::allRenewalPayments), the one endpoint that already
// joins and returns payer_name alongside payable_amount. That endpoint does a
// LIKE search, so results are filtered here to an exact payment_reference
// match. Gated by "view_payments" (same permission listPayments already
// requires). Returns null if no exact match is found.
export const searchRenewalPayment = async (reference) => {
  const ref = String(reference || "").trim();
  if (!ref) return null;
  const rows = await listPayments({ type: "renewal", search: ref });
  return rows.find((r) => String(r.payment_reference || "").trim() === ref) || null;
};

// GET /api/renewal-payments/{id}/shroff-voucher — operator/printable version
// of the same voucher as getMyRenewalShroffVoucherPdf above, keyed by
// renewal id alone since the caller already has admin access. Gated by
// "view_payments". responseType "blob" so axios doesn't try to JSON-parse
// the PDF.
export const getRenewalShroffVoucherPdf = (renewalId) =>
  api.get(`/api/renewal-payments/${renewalId}/shroff-voucher`, { responseType: "blob" }).then((r) => r.data);

// POST /api/renewal-payments/manual-confirm — multipart. payload:
// { payment_reference (required — from the member's own renewal row),
// paid_amount, paid_date, receipt_reference, receipt (File, required), note? }. Gated by
// "submit_manual_payment_receipts". Marks the renewal Paid and updates the
// member's membership dates/status immediately (HTTP 200, not a queued 202)
// — a manual_payment_receipts row is still queued for Director/Admin
// acknowledgement on the Payments -> Manual Approvals tab, but that step is
// audit-only now and doesn't gate the payment.
export const confirmRenewalManualPayment = (payload) =>
  api.post("/api/renewal-payments/manual-confirm", toManualPaymentFormData(payload)).then((r) => r.data);

// POST /api/renewal-payments/status — { renewal_id, payment_reference } (both
// required). Unauthenticated route (same as club-renewal-payments/status,
// booking-payments/status) polled while the CyberExt popup is open, to
// detect the moment the gateway's server-to-server callback
// (POST /api/renewal-payments/confirm) has flipped this renewal to Paid.
// Returns the same shape as one row from getMyRenewals — but like
// store/show/myRenewals (unlike the admin index() listing), this route never
// joins the payment_status category description, so `payment_status` comes
// back null either way; check `paid_at` instead (set the moment
// completeRenewal() runs).
export const checkRenewalPaymentStatus = ({ renewal_id, payment_reference }) =>
  api.post("/api/renewal-payments/status", { renewal_id, payment_reference }).then((r) => r.data);
